import React, { useCallback, useEffect, useMemo, useState, memo, Component } from 'react';
import { Plus, Building2, Users, FileText, X, Edit2, Trash2, Phone, Mail, User, Loader2, MapPin, Clock, Briefcase, CheckCircle2, AlertCircle, RefreshCw, DollarSign, Search, UtensilsCrossed, Check } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { SearchBar } from '../components/ui/SearchBar';
import { FilterDropdown } from '../components/ui/FilterDropdown';
import { useOrders } from '../contexts/OrderContext';
import { useActivityLog } from '../contexts/ActivityLogContext';
import { useToast } from '../components/ui/Toast';
import { useAuth } from '../contexts/AuthContext';
import { supabaseClient, isSupabaseConfigured } from '../lib/supabase';
import { useDocumentTitle } from '../utils/useDocumentTitle';
interface Branch {
  id: string;
  name: string;
  address: string;
  companyId: string;
  orderCutoffTime?: string;
  deliveryStartTime?: string;
  deliveryEndTime?: string;
}
interface Company {
  id: string;
  name: string;
  rnc: string;
  logo?: string;
  branches: Branch[];
  employees: number;
  taxExempt: boolean;
  subsidy: string;
  subsidyEnabled: boolean;
  orderCutoffTime: string;
  deliveryStartTime: string;
  deliveryEndTime: string;
  contactName?: string;
  contactEmail?: string;
  contactPhone?: string;
}
export function CompanySettings() {
  useDocumentTitle('Empresas');
  const {
    user
  } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [taxFilter, setTaxFilter] = useState('Todos');
  const [companies, setCompanies] = useState<Company[]>([]);
  const [employeeCounts, setEmployeeCounts] = useState<Record<string, number>>({});
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const {
    orders
  } = useOrders();
  const {
    addLog
  } = useActivityLog();
  const {
    showToast
  } = useToast();
  const [isAutoRefreshing, setIsAutoRefreshing] = useState(false);
  // Load companies and employee counts
  useEffect(() => {
    loadCompanies();
  }, []);
  // Real-time subscription to companies table
  useEffect(() => {
    if (!isSupabaseConfigured()) return;
    const subscription = supabaseClient.subscribeToCompanies(payload => {
      console.log('📡 Companies real-time update:', payload.eventType);
      // Reload silently without showing loading spinner
      loadCompaniesSilent();
    });
    return () => {
      subscription?.unsubscribe();
    };
  }, []);
  // Polling as backup every 10 seconds
  useEffect(() => {
    const interval = setInterval(async () => {
      setIsAutoRefreshing(true);
      try {
        await loadCompaniesSilent();
      } catch (error) {
        console.error('Error auto-refreshing companies:', error);
      } finally {
        setTimeout(() => setIsAutoRefreshing(false), 500);
      }
    }, 10000);
    return () => clearInterval(interval);
  }, []);
  const loadCompanies = async () => {
    setLoading(true);
    try {
      await loadCompaniesData();
    } finally {
      setLoading(false);
    }
  };
  // Silent reload (no loading spinner) for real-time updates
  const loadCompaniesSilent = async () => {
    try {
      await loadCompaniesData();
    } catch (error) {
      console.error('Error in silent reload:', error);
    }
  };
  const loadCompaniesData = async () => {
    try {
      if (isSupabaseConfigured()) {
        const companiesData = await supabaseClient.getCompanies();
        const employeesData = await supabaseClient.getEmployees();
        const counts: Record<string, number> = {};
        employeesData.forEach((emp: any) => {
          const companyId = emp.company_id;
          counts[companyId] = (counts[companyId] || 0) + 1;
        });
        setEmployeeCounts(counts);
        setCompanies(companiesData);
        localStorage.setItem('criket_companies', JSON.stringify(companiesData));
      } else {
        const saved = localStorage.getItem('criket_companies');
        setCompanies(saved ? JSON.parse(saved) : []);
      }
    } catch (error) {
      console.error('Error loading companies:', error);
      const saved = localStorage.getItem('criket_companies');
      setCompanies(saved ? JSON.parse(saved) : []);
    }
  };
  const getOrderCountByCompany = (companyName: string) => {
    return orders.filter(o => o.company === companyName).length;
  };
  const getEmployeeCountByCompany = (companyId: string) => {
    return employeeCounts[companyId] || 0;
  };
  const filteredCompanies = useMemo(() => {
    return companies.filter(c => {
      const matchesSearch = c.name.toLowerCase().includes(searchTerm.toLowerCase()) || c.rnc.includes(searchTerm);
      const matchesTax = taxFilter === 'Todos' || taxFilter === 'Exento ITBIS' && c.taxExempt || taxFilter === 'No Exento' && !c.taxExempt;
      return matchesSearch && matchesTax;
    });
  }, [companies, searchTerm, taxFilter]);
  const handleDetails = useCallback((company: Company) => {
    setSelectedCompany(company);
    setShowDetailsModal(true);
  }, []);
  const handleEdit = useCallback((company: Company) => {
    setSelectedCompany(company);
    setShowEditModal(true);
  }, []);
  const handleAddCompany = useCallback(() => {
    setSelectedCompany(null);
    setShowAddModal(true);
  }, []);
  const saveNewCompany = useCallback(async (formData: any) => {
    try {
      if (isSupabaseConfigured()) {
        await supabaseClient.createCompany({
          name: formData.name,
          rnc: formData.rnc,
          logo: formData.logo,
          branches: formData.branches,
          taxExempt: formData.taxExempt,
          subsidy: formData.subsidy,
          subsidyEnabled: formData.subsidyEnabled,
          orderCutoffTime: formData.orderCutoffTime,
          deliveryStartTime: formData.deliveryStartTime,
          deliveryEndTime: formData.deliveryEndTime,
          contactName: formData.contactName,
          contactEmail: formData.contactEmail,
          contactPhone: formData.contactPhone,
          companyPrices: formData.companyPrices,
          assignedCategories: formData.assignedCategories
        });
        if (formData.subsidyEnabled) {
          await supabaseClient.createBalanceLog({
            companyName: formData.name,
            actionType: 'subsidy_enabled',
            performedBy: user?.name || 'Admin',
            performedByRole: 'admin',
            notes: 'Subsidio activado al crear la empresa'
          });
        }
      }
      await loadCompanies();
      addLog('Empresa Creada', `${formData.name} - RNC: ${formData.rnc}`, 'company');
      showToast('success', 'Empresa creada', `"${formData.name}" ha sido registrada exitosamente`);
      setShowAddModal(false);
    } catch (error) {
      console.error('Error creating company:', error);
      showToast('error', 'Error', 'No se pudo crear la empresa. Intenta de nuevo.');
    }
  }, [addLog, showToast, user]);
  const saveEditedCompany = useCallback(async (formData: any) => {
    if (!selectedCompany) return;
    try {
      if (isSupabaseConfigured()) {
        await supabaseClient.updateCompany(selectedCompany.id, {
          name: formData.name,
          rnc: formData.rnc,
          logo: formData.logo,
          taxExempt: formData.taxExempt,
          subsidy: formData.subsidy,
          subsidyEnabled: formData.subsidyEnabled,
          orderCutoffTime: formData.orderCutoffTime,
          deliveryStartTime: formData.deliveryStartTime,
          deliveryEndTime: formData.deliveryEndTime,
          branches: formData.branches,
          contactName: formData.contactName,
          contactEmail: formData.contactEmail,
          contactPhone: formData.contactPhone,
          companyPrices: formData.companyPrices,
          assignedCategories: formData.assignedCategories
        });
        // Log subsidy status change if it changed
        if (selectedCompany.subsidyEnabled !== formData.subsidyEnabled) {
          await supabaseClient.createBalanceLog({
            companyId: selectedCompany.id,
            companyName: formData.name,
            actionType: formData.subsidyEnabled ? 'subsidy_enabled' : 'subsidy_disabled',
            performedBy: user?.name || 'Admin',
            performedByRole: 'admin',
            notes: formData.subsidyEnabled ? 'Subsidio activado' : 'Subsidio desactivado'
          });
        }
      }
      await loadCompanies();
      addLog('Empresa Actualizada', `${formData.name} - ${formData.branches.length} sucursales`, 'company');
      showToast('success', 'Empresa actualizada', `Los cambios en "${formData.name}" se han guardado`);
      setShowEditModal(false);
      setSelectedCompany(null);
    } catch (error) {
      console.error('Error updating company:', error);
      showToast('error', 'Error', 'No se pudo actualizar la empresa. Intenta de nuevo.');
    }
  }, [selectedCompany, addLog, user]);
  if (loading) {
    return <div className="flex items-center justify-center min-h-[400px]" data-id="element-2014">
        <div className="text-center" data-id="element-2015">
          <Loader2 className="w-8 h-8 animate-spin text-gray-500 mx-auto mb-4" data-id="element-2016" />
          <p className="text-gray-500" data-id="element-2017">Cargando empresas...</p>
        </div>
      </div>;
  }
  return <div className="p-6 lg:p-10 max-w-7xl mx-auto space-y-8" data-id="element-2018">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-2019">
        <div data-id="element-2020">
          <div className="flex items-center gap-2" data-id="element-2021">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white tracking-tight" data-id="element-2022">
              Gestión de Empresas
            </h1>
            {isAutoRefreshing && <RefreshCw className="w-4 h-4 text-blue-500 animate-spin" data-id="element-2023" />}
          </div>
          <p className="text-gray-500 dark:text-gray-400 mt-2 text-lg" data-id="element-2024">
            Administra las empresas clientes y sus configuraciones
          </p>
        </div>
        <Button leftIcon={<Plus size={18} data-id="element-2026" />} onClick={handleAddCompany} size="lg" className="shadow-lg shadow-gray-900/10" data-id="element-2025">
          Nueva Empresa
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row items-stretch sm:items-end gap-4 bg-white dark:bg-gray-900 p-4 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm" data-id="element-2027">
        <SearchBar placeholder="Buscar por nombre o RNC..." className="w-full sm:flex-1" onSearch={setSearchTerm} data-id="element-2028" />
        <FilterDropdown value={taxFilter} options={['Todos', 'Exento ITBIS', 'No Exento']} onChange={setTaxFilter} className="w-full sm:w-56" data-id="element-2029" />
      </div>

      {filteredCompanies.length === 0 ? <div className="text-center py-24 bg-white dark:bg-gray-900 rounded-2xl border border-dashed border-gray-200 dark:border-gray-800" data-id="element-2030">
          <div className="w-16 h-16 bg-gray-50 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-2031">
            <Building2 size={32} className="text-gray-400" data-id="element-2032" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-2033">
            No se encontraron empresas
          </h3>
          <p className="text-gray-500 dark:text-gray-400 mt-2" data-id="element-2034">
            {searchTerm || taxFilter !== 'Todos' ? 'Intenta con otros filtros' : 'Agrega tu primera empresa para comenzar'}
          </p>
        </div> : <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-2035">
          {filteredCompanies.map(company => {
        const orderCount = getOrderCountByCompany(company.name);
        const employeeCount = getEmployeeCountByCompany(company.id);
        return <CompanyCard key={company.id} company={company} orderCount={orderCount} employeeCount={employeeCount} onEdit={handleEdit} onDetails={handleDetails} data-id="element-2036" />;
      })}
        </div>}

      {/* Modals */}
      {(showAddModal || showEditModal) && <CompanyFormModal company={selectedCompany} onClose={() => {
      setShowAddModal(false);
      setShowEditModal(false);
      setSelectedCompany(null);
    }} onSave={showAddModal ? saveNewCompany : saveEditedCompany} data-id="element-2037" />}

      {showDetailsModal && selectedCompany && <CompanyDetailsModal company={selectedCompany} orderCount={getOrderCountByCompany(selectedCompany.name)} employeeCount={getEmployeeCountByCompany(selectedCompany.id)} onClose={() => {
      setShowDetailsModal(false);
      setSelectedCompany(null);
    }} data-id="element-2038" />}
    </div>;
}
// Company Card Component
function CompanyCard({
  company,
  orderCount,
  employeeCount,
  onEdit,
  onDetails
}: any) {
  return <Card className="p-5 hover:shadow-xl transition-all duration-300 flex flex-col h-full border border-gray-200 dark:border-gray-800 group" data-id="element-2039">
      <div className="flex justify-between items-start mb-5" data-id="element-2040">
        <div className="flex items-center gap-4" data-id="element-2041">
          <div className="w-14 h-14 rounded-xl bg-gray-50 dark:bg-gray-800 flex items-center justify-center border border-gray-100 dark:border-gray-700 overflow-hidden" data-id="element-2042">
            {company.logo ? <img src={company.logo} alt={company.name} className="w-full h-full object-contain p-2" data-id="element-2043" /> : <Building2 className="w-7 h-7 text-gray-400" data-id="element-2044" />}
          </div>
          <div data-id="element-2045">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white leading-tight group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors" data-id="element-2046">
              {company.name}
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 font-mono mt-1" data-id="element-2047">
              {company.rnc}
            </p>
          </div>
        </div>

        {company.taxExempt && <span className="flex items-center gap-1 px-2.5 py-1 text-[10px] uppercase font-bold tracking-wide rounded-full bg-green-50 text-green-700 border border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800" data-id="element-2048">
            <CheckCircle2 size={10} data-id="element-2049" /> Exento
          </span>}
        {company.subsidyEnabled && <span className="flex items-center gap-1 px-2.5 py-1 text-[10px] uppercase font-bold tracking-wide rounded-full bg-blue-50 text-blue-700 border border-blue-200 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-800 ml-2" data-id="element-2050">
            <DollarSign size={10} data-id="element-2051" /> Subsidio Activo
          </span>}
      </div>

      <div className="grid grid-cols-2 gap-3 mb-5" data-id="element-2052">
        <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-100 dark:border-gray-700" data-id="element-2053">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1 flex items-center gap-1.5" data-id="element-2054">
            <Users size={12} data-id="element-2055" /> Empleados
          </p>
          <p className="text-lg font-bold text-gray-900 dark:text-white" data-id="element-2056">
            {employeeCount}
          </p>
        </div>
        <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-100 dark:border-gray-700" data-id="element-2057">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1 flex items-center gap-1.5" data-id="element-2058">
            <FileText size={12} data-id="element-2059" /> Pedidos
          </p>
          <p className="text-lg font-bold text-gray-900 dark:text-white" data-id="element-2060">
            {orderCount}
          </p>
        </div>
      </div>

      <div className="mb-5 flex-1" data-id="element-2061">
        <div className="flex items-center justify-between mb-2" data-id="element-2062">
          <p className="text-xs font-bold text-gray-400 uppercase tracking-wider" data-id="element-2063">
            Sucursales
          </p>
          <span className="text-xs font-medium text-gray-500 bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded-full" data-id="element-2064">
            {company.branches?.length || 0}
          </span>
        </div>
        <div className="flex flex-wrap gap-2" data-id="element-2065">
          {company.branches?.slice(0, 3).map((branch: Branch) => <span key={branch.id} className="px-2.5 py-1 text-xs font-medium rounded-md bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 flex items-center gap-1.5" data-id="element-2066">
              <MapPin size={10} className="text-gray-400" data-id="element-2067" />
              {branch.name}
            </span>)}
          {company.branches?.length > 3 && <span className="px-2.5 py-1 text-xs font-medium rounded-md bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-700" data-id="element-2068">
              +{company.branches.length - 3}
            </span>}
          {(!company.branches || company.branches.length === 0) && <span className="text-xs text-gray-400 italic" data-id="element-2069">
              Sin sucursales registradas
            </span>}
        </div>
      </div>

      <div className="mt-auto pt-4 border-t border-gray-100 dark:border-gray-800 flex gap-3" data-id="element-2070">
        <Button variant="outline" size="sm" fullWidth onClick={() => onEdit(company)} className="h-9" data-id="element-2071">
          Editar
        </Button>
        <Button variant="ghost" size="sm" fullWidth onClick={() => onDetails(company)} className="h-9" data-id="element-2072">
          Detalles
        </Button>
      </div>
    </Card>;
}
// Company Form Modal Component
function CompanyFormModal({
  company,
  onClose,
  onSave
}: any) {
  const {
    showToast
  } = useToast();
  const {
    user
  } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [formData, setFormData] = useState(() => ({
    name: company?.name || '',
    rnc: company?.rnc || '',
    logo: company?.logo || '',
    subsidy: company?.subsidy || 'RD$ 0',
    subsidyEnabled: company?.subsidyEnabled || false,
    taxExempt: company?.taxExempt || false,
    orderCutoffTime: company?.orderCutoffTime || '23:00',
    deliveryStartTime: company?.deliveryStartTime || '12:00',
    deliveryEndTime: company?.deliveryEndTime || '14:00',
    contactName: company?.contactName || '',
    contactEmail: company?.contactEmail || '',
    contactPhone: company?.contactPhone || '',
    branches: company?.branches || [],
    companyPrices: company?.companyPrices || {},
    assignedCategories: company?.assignedCategories || []
  }));
  const [newBranch, setNewBranch] = useState({
    name: '',
    address: '',
    orderCutoffTime: '',
    deliveryStartTime: '',
    deliveryEndTime: ''
  });
  const [editingBranchIndex, setEditingBranchIndex] = useState<number | null>(null);
  // Menu items for pricing
  const [menuItems, setMenuItems] = useState<any[]>([]);
  const [loadingMenu, setLoadingMenu] = useState(false);
  const [priceSearch, setPriceSearch] = useState('');
  // Menu categories for assignment
  const [allCategories, setAllCategories] = useState<string[]>([]);
  const [categorySearch, setCategorySearch] = useState('');
  // Load menu items for pricing section
  useEffect(() => {
    const loadMenu = async () => {
      setLoadingMenu(true);
      try {
        if (isSupabaseConfigured()) {
          const [items, cats] = await Promise.all([supabaseClient.getMenuItems(), supabaseClient.getMenuCategories()]);
          setMenuItems(items);
          setAllCategories(cats.length > 0 ? cats : []);
        } else {
          const saved = localStorage.getItem('criket_menu_items');
          setMenuItems(saved ? JSON.parse(saved) : []);
          const savedCats = localStorage.getItem('criket_menu_categories');
          setAllCategories(savedCats ? JSON.parse(savedCats) : []);
        }
      } catch (e) {
        console.error('Error loading menu for pricing:', e);
      } finally {
        setLoadingMenu(false);
      }
    };
    loadMenu();
  }, []);
  const filteredMenuItems = menuItems.filter((item: any) => item.name.toLowerCase().includes(priceSearch.toLowerCase()) || item.category?.toLowerCase().includes(priceSearch.toLowerCase()));
  const filteredCategories = allCategories.filter(cat => cat.toLowerCase().includes(categorySearch.toLowerCase()));
  const handlePriceChange = (menuItemId: string, price: string) => {
    setFormData(prev => {
      const newPrices = {
        ...prev.companyPrices
      };
      if (!price || price === '' || parseFloat(price) <= 0) {
        delete newPrices[menuItemId];
      } else {
        newPrices[menuItemId] = parseFloat(price);
      }
      return {
        ...prev,
        companyPrices: newPrices
      };
    });
  };
  const customPriceCount = Object.keys(formData.companyPrices).length;
  const handleChange = useCallback((field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);
  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  }, [formData, onSave]);
  const toggleCategory = (categoryName: string) => {
    setFormData(prev => {
      const current = prev.assignedCategories || [];
      if (current.includes(categoryName)) {
        return {
          ...prev,
          assignedCategories: current.filter((c: string) => c !== categoryName)
        };
      } else {
        return {
          ...prev,
          assignedCategories: [...current, categoryName]
        };
      }
    });
  };
  const toggleAllCategories = () => {
    setFormData(prev => {
      const current = prev.assignedCategories || [];
      if (current.length === allCategories.length) {
        return {
          ...prev,
          assignedCategories: []
        };
      } else {
        return {
          ...prev,
          assignedCategories: [...allCategories]
        };
      }
    });
  };
  const assignedCount = (formData.assignedCategories || []).length;
  const addBranch = useCallback(() => {
    if (!newBranch.name || !newBranch.address) {
      showToast('warning', 'Campos requeridos', 'Completa el nombre y dirección de la sucursal');
      return;
    }
    const branch: Branch = {
      id: `branch-${Date.now()}`,
      name: newBranch.name,
      address: newBranch.address,
      companyId: company?.id || '0',
      orderCutoffTime: newBranch.orderCutoffTime || formData.orderCutoffTime,
      deliveryStartTime: newBranch.deliveryStartTime || formData.deliveryStartTime,
      deliveryEndTime: newBranch.deliveryEndTime || formData.deliveryEndTime
    };
    if (editingBranchIndex !== null) {
      const updatedBranches = [...formData.branches];
      updatedBranches[editingBranchIndex] = {
        ...updatedBranches[editingBranchIndex],
        ...branch
      };
      setFormData(prev => ({
        ...prev,
        branches: updatedBranches
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        branches: [...prev.branches, branch]
      }));
    }
    setEditingBranchIndex(null);
    setNewBranch({
      name: '',
      address: '',
      orderCutoffTime: '',
      deliveryStartTime: '',
      deliveryEndTime: ''
    });
  }, [newBranch, editingBranchIndex, company, formData]);
  const editBranch = useCallback((index: number) => {
    const branch = formData.branches[index];
    setNewBranch({
      name: branch.name,
      address: branch.address,
      orderCutoffTime: branch.orderCutoffTime || '',
      deliveryStartTime: branch.deliveryStartTime || '',
      deliveryEndTime: branch.deliveryEndTime || ''
    });
    setEditingBranchIndex(index);
  }, [formData.branches]);
  const removeBranch = useCallback((index: number) => {
    setFormData(prev => ({
      ...prev,
      branches: prev.branches.filter((_: any, i: number) => i !== index)
    }));
  }, []);
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 overflow-y-auto" data-id="element-2073">
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-4xl border border-gray-200 dark:border-gray-800 my-8 flex flex-col max-h-[90vh]" data-id="element-2074">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-800 sticky top-0 bg-white dark:bg-gray-900 z-10 rounded-t-2xl" data-id="element-2075">
          <div data-id="element-2076">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-2077">
              {company ? 'Editar Empresa' : 'Nueva Empresa'}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1" data-id="element-2078">
              Completa la información de la empresa y sus sucursales
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors" data-id="element-2079">
            <X size={20} data-id="element-2080" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-8 overflow-y-auto flex-1" data-id="element-2081">
          {/* Basic Info */}
          <section data-id="element-2082">
            <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2 uppercase tracking-wider" data-id="element-2083">
              <Briefcase size={16} data-id="element-2084" /> Información General
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-2085">
              <div data-id="element-2086">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5" data-id="element-2087">
                  Nombre de la Empresa
                </label>
                <input type="text" value={formData.name} onChange={e => handleChange('name', e.target.value)} className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white transition-all" placeholder="Ej. Tech Solutions SRL" required data-id="element-2088" />
              </div>
              <div data-id="element-2089">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5" data-id="element-2090">
                  RNC
                </label>
                <input type="text" value={formData.rnc} onChange={e => handleChange('rnc', e.target.value)} className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white transition-all font-mono" placeholder="1-01-00000-0" required data-id="element-2091" />
              </div>
              <div className="md:col-span-2" data-id="element-2092">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5" data-id="element-2093">
                  URL del Logo
                </label>
                <input type="url" value={formData.logo} onChange={e => handleChange('logo', e.target.value)} className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white transition-all" placeholder="https://ejemplo.com/logo.png" data-id="element-2094" />
              </div>
              <div data-id="element-2095">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5" data-id="element-2096">
                  Subsidio
                </label>
                <input type="text" value={formData.subsidy} onChange={e => handleChange('subsidy', e.target.value)} className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white transition-all" placeholder="RD$ 200 o 50%" data-id="element-2097" />
              </div>

              {isAdmin && <div className="flex items-center pt-6" data-id="element-2098">
                  <label className="flex items-center gap-3 cursor-pointer p-3 border border-gray-200 dark:border-gray-700 rounded-xl w-full hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors" data-id="element-2099">
                    <div className={`w-10 h-6 rounded-full p-1 transition-colors ${formData.subsidyEnabled ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'}`} data-id="element-2100">
                      <div className={`bg-white w-4 h-4 rounded-full shadow-sm transform transition-transform ${formData.subsidyEnabled ? 'translate-x-4' : 'translate-x-0'}`} data-id="element-2101" />
                    </div>
                    <input type="checkbox" checked={formData.subsidyEnabled} onChange={e => handleChange('subsidyEnabled', e.target.checked)} className="hidden" data-id="element-2102" />
                    <div data-id="element-2103">
                      <span className="block text-sm font-bold text-gray-900 dark:text-white" data-id="element-2104">
                        Subsidio Activo
                      </span>
                      <span className="block text-xs text-gray-500 dark:text-gray-400" data-id="element-2105">
                        Permite asignar balance mensual a empleados
                      </span>
                    </div>
                  </label>
                </div>}

              <div className="flex items-center pt-6" data-id="element-2106">
                <label className="flex items-center gap-3 cursor-pointer p-3 border border-gray-200 dark:border-gray-700 rounded-xl w-full hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors" data-id="element-2107">
                  <input type="checkbox" checked={formData.taxExempt} onChange={e => handleChange('taxExempt', e.target.checked)} className="w-5 h-5 rounded border-gray-300 text-black focus:ring-black" data-id="element-2108" />
                  <div data-id="element-2109">
                    <span className="block text-sm font-bold text-gray-900 dark:text-white" data-id="element-2110">
                      Exento de ITBIS
                    </span>
                    <span className="block text-xs text-gray-500 dark:text-gray-400" data-id="element-2111">
                      Esta empresa no paga impuestos
                    </span>
                  </div>
                </label>
              </div>
            </div>
          </section>

          <div className="h-px bg-gray-100 dark:bg-gray-800" data-id="element-2112" />

          {/* Contact Info */}
          <section data-id="element-2113">
            <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2 uppercase tracking-wider" data-id="element-2114">
              <User size={16} data-id="element-2115" /> Contacto
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-id="element-2116">
              <div data-id="element-2117">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5" data-id="element-2118">
                  Nombre
                </label>
                <input type="text" value={formData.contactName} onChange={e => handleChange('contactName', e.target.value)} className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white transition-all" placeholder="Juan Pérez" data-id="element-2119" />
              </div>
              <div data-id="element-2120">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5" data-id="element-2121">
                  Email
                </label>
                <input type="email" value={formData.contactEmail} onChange={e => handleChange('contactEmail', e.target.value)} className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white transition-all" placeholder="contacto@empresa.com" data-id="element-2122" />
              </div>
              <div data-id="element-2123">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5" data-id="element-2124">
                  Teléfono
                </label>
                <input type="tel" value={formData.contactPhone} onChange={e => handleChange('contactPhone', e.target.value)} className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white transition-all" placeholder="809-555-0000" data-id="element-2125" />
              </div>
            </div>
          </section>

          <div className="h-px bg-gray-100 dark:bg-gray-800" data-id="element-2126" />

          {/* Schedule */}
          <section data-id="element-2127">
            <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-2 flex items-center gap-2 uppercase tracking-wider" data-id="element-2128">
              <Clock size={16} data-id="element-2129" /> Horarios Predeterminados
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-4" data-id="element-2130">
              Configura cuándo los empleados pueden ver el menú, hasta cuándo
              pueden pedir, y a qué hora se entrega.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5" data-id="element-2131">
              <div data-id="element-2132">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5" data-id="element-2133">
                  Inicio de Pedidos
                </label>
                <input type="time" value={formData.deliveryStartTime} onChange={e => handleChange('deliveryStartTime', e.target.value)} className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white transition-all" data-id="element-2134" />
                <p className="text-[11px] text-gray-400 mt-1" data-id="element-2135">
                  Hora en que los empleados pueden ver el menú
                </p>
              </div>
              <div data-id="element-2136">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5" data-id="element-2137">
                  Hora de Cierre
                </label>
                <input type="time" value={formData.orderCutoffTime} onChange={e => handleChange('orderCutoffTime', e.target.value)} className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white transition-all" data-id="element-2138" />
                <p className="text-[11px] text-gray-400 mt-1" data-id="element-2139">
                  Hora límite para hacer pedidos
                </p>
              </div>
              <div data-id="element-2140">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5" data-id="element-2141">
                  Hora de Entrega
                </label>
                <input type="time" value={formData.deliveryEndTime} onChange={e => handleChange('deliveryEndTime', e.target.value)} className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white transition-all" data-id="element-2142" />
                <p className="text-[11px] text-gray-400 mt-1" data-id="element-2143">
                  Hora que se muestra al empleado como entrega estimada
                </p>
              </div>
            </div>
          </section>

          <div className="h-px bg-gray-100 dark:bg-gray-800" data-id="element-2144" />

          {/* Custom Pricing Section */}
          <section data-id="element-2145">
            <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-2 flex items-center gap-2 uppercase tracking-wider" data-id="element-2146">
              <DollarSign size={16} data-id="element-2147" /> Precios Personalizados
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-4" data-id="element-2148">
              Asigna precios especiales para esta empresa. Si dejas un campo
              vacío, se usará el precio base del menú.
            </p>

            {customPriceCount > 0 && <div className="mb-4 flex items-center gap-2 px-3 py-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl" data-id="element-2149">
                <DollarSign size={14} className="text-blue-600" data-id="element-2150" />
                <span className="text-xs font-bold text-blue-700 dark:text-blue-300" data-id="element-2151">
                  {customPriceCount} precio{customPriceCount !== 1 ? 's' : ''}{' '}
                  personalizado{customPriceCount !== 1 ? 's' : ''}
                </span>
              </div>}

            <div className="mb-4" data-id="element-2152">
              <div className="relative" data-id="element-2153">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} data-id="element-2154" />
                <input type="text" value={priceSearch} onChange={e => setPriceSearch(e.target.value)} placeholder="Buscar plato..." className="w-full pl-10 pr-4 py-2.5 border border-gray-300 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm" data-id="element-2155" />
              </div>
            </div>

            {loadingMenu ? <div className="flex justify-center py-8" data-id="element-2156">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" data-id="element-2157" />
              </div> : filteredMenuItems.length === 0 ? <div className="text-center py-8 text-gray-400 text-sm" data-id="element-2158">
                {priceSearch ? 'No se encontraron platos' : 'No hay platos en el menú'}
              </div> : <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1" data-id="element-2159">
                {filteredMenuItems.map((item: any) => {
              const hasCustomPrice = formData.companyPrices[item.id] !== undefined;
              return <div key={item.id} className={`flex items-center gap-3 p-3 rounded-xl border transition-colors ${hasCustomPrice ? 'bg-blue-50 dark:bg-blue-900/10 border-blue-200 dark:border-blue-800' : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'}`} data-id="element-2160">
                      {item.image ? <img src={item.image} alt="" className="w-10 h-10 rounded-lg object-cover shrink-0" data-id="element-2161" /> : <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center shrink-0" data-id="element-2162">
                          <UtensilsCrossed size={16} className="text-gray-400" data-id="element-2163" />
                        </div>}
                      <div className="flex-1 min-w-0" data-id="element-2164">
                        <p className="text-sm font-bold text-gray-900 dark:text-white truncate" data-id="element-2165">
                          {item.name}
                        </p>
                        <p className="text-[11px] text-gray-500 dark:text-gray-400" data-id="element-2166">
                          {item.category} · Precio base:{' '}
                          <span className="font-mono font-bold" data-id="element-2167">
                            RD$ {parseFloat(item.price).toFixed(2)}
                          </span>
                        </p>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0" data-id="element-2168">
                        <span className="text-xs text-gray-400 font-bold" data-id="element-2169">
                          RD$
                        </span>
                        <input type="number" step="0.01" min="0" value={formData.companyPrices[item.id] ?? ''} onChange={e => handlePriceChange(item.id, e.target.value)} placeholder={parseFloat(item.price).toFixed(0)} className={`w-24 px-3 py-2 text-sm font-mono font-bold text-right border rounded-lg focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white transition-colors ${hasCustomPrice ? 'border-blue-300 dark:border-blue-700 bg-white dark:bg-gray-800 text-blue-700 dark:text-blue-300' : 'border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white'}`} data-id="element-2170" />
                      </div>
                    </div>;
            })}
              </div>}
          </section>

          <div className="h-px bg-gray-100 dark:bg-gray-800" data-id="element-2171" />

          {/* Assigned Categories Section */}
          <section data-id="element-2172">
            <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-2 flex items-center gap-2 uppercase tracking-wider" data-id="element-2173">
              <UtensilsCrossed size={16} data-id="element-2174" /> Categorías del Menú
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-4" data-id="element-2175">
              Selecciona qué categorías del menú pueden ver los empleados de
              esta empresa. Si no seleccionas ninguna, verán todas las
              categorías.
            </p>

            {assignedCount > 0 && <div className="mb-4 flex items-center gap-2 px-3 py-2 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl" data-id="element-2176">
                <UtensilsCrossed size={14} className="text-emerald-600" data-id="element-2177" />
                <span className="text-xs font-bold text-emerald-700 dark:text-emerald-300" data-id="element-2178">
                  {assignedCount} categoría{assignedCount !== 1 ? 's' : ''}{' '}
                  asignada{assignedCount !== 1 ? 's' : ''}
                </span>
              </div>}

            {assignedCount === 0 && allCategories.length > 0 && <div className="mb-4 flex items-center gap-2 px-3 py-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl" data-id="element-2179">
                <AlertCircle size={14} className="text-blue-600" data-id="element-2180" />
                <span className="text-xs font-bold text-blue-700 dark:text-blue-300" data-id="element-2181">
                  Sin restricción — los empleados verán todas las categorías
                </span>
              </div>}

            <div className="mb-4 flex gap-2 items-center" data-id="element-2182">
              <div className="relative flex-1" data-id="element-2183">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} data-id="element-2184" />
                <input type="text" value={categorySearch} onChange={e => setCategorySearch(e.target.value)} placeholder="Buscar categoría..." className="w-full pl-10 pr-4 py-2.5 border border-gray-300 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm" data-id="element-2185" />
              </div>
              {allCategories.length > 0 && <button type="button" onClick={toggleAllCategories} className={`px-3 py-2.5 rounded-xl text-xs font-bold transition-colors border whitespace-nowrap ${assignedCount === allCategories.length ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-300 dark:border-emerald-800' : 'bg-gray-100 text-gray-600 border-gray-200 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700'}`} data-id="element-2186">
                  {assignedCount === allCategories.length ? 'Desmarcar Todo' : 'Seleccionar Todo'}
                </button>}
            </div>

            {loadingMenu ? <div className="flex justify-center py-8" data-id="element-2187">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" data-id="element-2188" />
              </div> : allCategories.length === 0 ? <div className="text-center py-8 text-gray-400 text-sm" data-id="element-2189">
                No hay categorías en el menú
              </div> : <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[250px] overflow-y-auto pr-1" data-id="element-2190">
                {filteredCategories.map(cat => {
              const isSelected = (formData.assignedCategories || []).includes(cat);
              const itemCount = menuItems.filter(item => item.category === cat).length;
              return <div key={cat} onClick={() => toggleCategory(cat)} className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${isSelected ? 'bg-emerald-50 dark:bg-emerald-900/10 border-emerald-200 dark:border-emerald-800' : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'}`} data-id="element-2191">
                      <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-colors shrink-0 ${isSelected ? 'bg-emerald-500 border-emerald-500 text-white' : 'border-gray-300 dark:border-gray-600'}`} data-id="element-2192">
                        {isSelected && <Check size={14} strokeWidth={3} data-id="element-2193" />}
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-2194">
                        <p className={`text-sm font-bold truncate ${isSelected ? 'text-emerald-900 dark:text-emerald-100' : 'text-gray-900 dark:text-white'}`} data-id="element-2195">
                          {cat}
                        </p>
                        <p className="text-[11px] text-gray-500 dark:text-gray-400" data-id="element-2196">
                          {itemCount} plato{itemCount !== 1 ? 's' : ''}
                        </p>
                      </div>
                    </div>;
            })}
              </div>}
          </section>

          <div className="h-px bg-gray-100 dark:bg-gray-800" data-id="element-2197" />

          {/* Branches */}
          <section data-id="element-2198">
            <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2 uppercase tracking-wider" data-id="element-2199">
              <MapPin size={16} data-id="element-2200" /> Sucursales
            </h3>
            <div className="bg-gray-50 dark:bg-gray-800/50 p-5 rounded-2xl mb-4 border border-gray-200 dark:border-gray-700" data-id="element-2201">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-2202">
                <input type="text" placeholder="Nombre de sucursal" value={newBranch.name} onChange={e => setNewBranch({
                ...newBranch,
                name: e.target.value
              })} className="px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white" data-id="element-2203" />
                <input type="text" placeholder="Dirección" value={newBranch.address} onChange={e => setNewBranch({
                ...newBranch,
                address: e.target.value
              })} className="px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white" data-id="element-2204" />
              </div>
              <Button type="button" size="sm" onClick={addBranch} leftIcon={<Plus size={14} data-id="element-2206" />} className="w-full md:w-auto" data-id="element-2205">
                {editingBranchIndex !== null ? 'Actualizar Sucursal' : 'Agregar Sucursal'}
              </Button>
            </div>

            <div className="space-y-3" data-id="element-2207">
              {formData.branches.map((branch: Branch, index: number) => <div key={branch.id || index} className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl hover:shadow-sm transition-shadow" data-id="element-2208">
                  <div className="flex-1" data-id="element-2209">
                    <p className="font-bold text-gray-900 dark:text-white text-sm flex items-center gap-2" data-id="element-2210">
                      <MapPin size={14} className="text-gray-400" data-id="element-2211" />
                      {branch.name}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 pl-6" data-id="element-2212">
                      {branch.address}
                    </p>
                  </div>
                  <div className="flex gap-2" data-id="element-2213">
                    <button type="button" onClick={() => editBranch(index)} className="p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors" data-id="element-2214">
                      <Edit2 size={16} data-id="element-2215" />
                    </button>
                    <button type="button" onClick={() => removeBranch(index)} className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors" data-id="element-2216">
                      <Trash2 size={16} data-id="element-2217" />
                    </button>
                  </div>
                </div>)}
              {formData.branches.length === 0 && <div className="text-center py-6 text-gray-400 dark:text-gray-500 text-sm italic" data-id="element-2218">
                  No hay sucursales agregadas
                </div>}
            </div>
          </section>
        </form>

        <div className="p-6 border-t border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 rounded-b-2xl flex gap-3" data-id="element-2219">
          <Button type="button" variant="outline" onClick={onClose} fullWidth data-id="element-2220">
            Cancelar
          </Button>
          <Button type="submit" fullWidth onClick={handleSubmit} data-id="element-2221">
            {company ? 'Guardar Cambios' : 'Crear Empresa'}
          </Button>
        </div>
      </div>
    </div>;
}
// Company Details Modal
function CompanyDetailsModal({
  company,
  orderCount,
  employeeCount,
  onClose
}: any) {
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-id="element-2222">
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-2xl border border-gray-200 dark:border-gray-800 max-h-[90vh] overflow-y-auto" data-id="element-2223">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-800 sticky top-0 bg-white dark:bg-gray-900 z-10" data-id="element-2224">
          <div className="flex items-center gap-4" data-id="element-2225">
            <div className="w-12 h-12 rounded-xl bg-gray-50 dark:bg-gray-800 flex items-center justify-center border border-gray-100 dark:border-gray-700" data-id="element-2226">
              {company.logo ? <img src={company.logo} alt={company.name} className="w-full h-full object-contain p-2" data-id="element-2227" /> : <Building2 className="w-6 h-6 text-gray-400" data-id="element-2228" />}
            </div>
            <div data-id="element-2229">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-2230">
                {company.name}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 font-mono" data-id="element-2231">
                {company.rnc}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors" data-id="element-2232">
            <X size={20} data-id="element-2233" />
          </button>
        </div>

        <div className="p-6 space-y-8" data-id="element-2234">
          <div data-id="element-2235">
            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4" data-id="element-2236">
              Información General
            </h3>
            <div className="grid grid-cols-2 gap-4" data-id="element-2237">
              <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700" data-id="element-2238">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1" data-id="element-2239">
                  Estado Fiscal
                </p>
                <p className="font-bold text-gray-900 dark:text-white flex items-center gap-2" data-id="element-2240">
                  {company.taxExempt ? <>
                      <CheckCircle2 size={16} className="text-green-500" data-id="element-2241" />{' '}
                      Exento ITBIS
                    </> : <>
                      <AlertCircle size={16} className="text-blue-500" data-id="element-2242" /> ITBIS
                      Aplica
                    </>}
                </p>
              </div>
              <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700" data-id="element-2243">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1" data-id="element-2244">
                  Subsidio
                </p>
                <div className="flex items-center justify-between" data-id="element-2245">
                  <p className="font-bold text-gray-900 dark:text-white" data-id="element-2246">
                    {company.subsidy}
                  </p>
                  {company.subsidyEnabled ? <span className="text-[10px] font-bold text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full" data-id="element-2247">
                      ACTIVO
                    </span> : <span className="text-[10px] font-bold text-gray-500 bg-gray-200 px-2 py-0.5 rounded-full" data-id="element-2248">
                      INACTIVO
                    </span>}
                </div>
              </div>
            </div>
          </div>

          {(company.contactName || company.contactEmail || company.contactPhone) && <div data-id="element-2249">
              <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4" data-id="element-2250">
                Contacto
              </h3>
              <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-4 space-y-3" data-id="element-2251">
                {company.contactName && <div className="flex items-center gap-3" data-id="element-2252">
                    <div className="w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center" data-id="element-2253">
                      <User size={14} className="text-gray-600 dark:text-gray-400" data-id="element-2254" />
                    </div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-2255">
                      {company.contactName}
                    </p>
                  </div>}
                {company.contactEmail && <div className="flex items-center gap-3" data-id="element-2256">
                    <div className="w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center" data-id="element-2257">
                      <Mail size={14} className="text-gray-600 dark:text-gray-400" data-id="element-2258" />
                    </div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-2259">
                      {company.contactEmail}
                    </p>
                  </div>}
                {company.contactPhone && <div className="flex items-center gap-3" data-id="element-2260">
                    <div className="w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center" data-id="element-2261">
                      <Phone size={14} className="text-gray-600 dark:text-gray-400" data-id="element-2262" />
                    </div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-2263">
                      {company.contactPhone}
                    </p>
                  </div>}
              </div>
            </div>}

          <div data-id="element-2264">
            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4" data-id="element-2265">
              Horarios
            </h3>
            <div className="grid grid-cols-3 gap-4" data-id="element-2266">
              <div className="text-center p-3 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-100 dark:border-blue-800" data-id="element-2267">
                <p className="text-[10px] font-bold text-blue-600 dark:text-blue-400 uppercase mb-1" data-id="element-2268">
                  Inicio Pedidos
                </p>
                <p className="text-lg font-bold text-blue-900 dark:text-blue-100" data-id="element-2269">
                  {company.deliveryStartTime}
                </p>
              </div>
              <div className="text-center p-3 bg-orange-50 dark:bg-orange-900/20 rounded-xl border border-orange-100 dark:border-orange-800" data-id="element-2270">
                <p className="text-[10px] font-bold text-orange-600 dark:text-orange-400 uppercase mb-1" data-id="element-2271">
                  Hora de Cierre
                </p>
                <p className="text-lg font-bold text-orange-900 dark:text-orange-100" data-id="element-2272">
                  {company.orderCutoffTime}
                </p>
              </div>
              <div className="text-center p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-xl border border-emerald-100 dark:border-emerald-800" data-id="element-2273">
                <p className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400 uppercase mb-1" data-id="element-2274">
                  Hora de Entrega
                </p>
                <p className="text-lg font-bold text-emerald-900 dark:text-emerald-100" data-id="element-2275">
                  {company.deliveryEndTime}
                </p>
              </div>
            </div>
          </div>

          <div data-id="element-2276">
            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4" data-id="element-2277">
              Sucursales ({company.branches?.length || 0})
            </h3>
            <div className="space-y-2" data-id="element-2278">
              {company.branches?.map((branch: Branch) => <div key={branch.id} className="p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 flex items-center gap-3" data-id="element-2279">
                  <MapPin size={18} className="text-gray-400" data-id="element-2280" />
                  <div data-id="element-2281">
                    <p className="font-bold text-gray-900 dark:text-white text-sm" data-id="element-2282">
                      {branch.name}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-2283">
                      {branch.address}
                    </p>
                  </div>
                </div>)}
            </div>
          </div>

          {/* Assigned Categories */}
          <div data-id="element-2284">
            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4" data-id="element-2285">
              Categorías del Menú
            </h3>
            {company.assignedCategories && company.assignedCategories.length > 0 ? <div className="flex flex-wrap gap-2" data-id="element-2286">
                {company.assignedCategories.map((cat: string) => <span key={cat} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-300 dark:border-emerald-800" data-id="element-2287">
                    <UtensilsCrossed size={12} data-id="element-2288" />
                    {cat}
                  </span>)}
              </div> : <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl" data-id="element-2289">
                <AlertCircle size={14} className="text-blue-600" data-id="element-2290" />
                <span className="text-xs font-medium text-blue-700 dark:text-blue-300" data-id="element-2291">
                  Sin restricción — los empleados ven todas las categorías
                </span>
              </div>}
          </div>

          <div data-id="element-2292">
            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4" data-id="element-2293">
              Estadísticas
            </h3>
            <div className="grid grid-cols-2 gap-4" data-id="element-2294">
              <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700" data-id="element-2295">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1" data-id="element-2296">
                  Total de Pedidos
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-2297">
                  {orderCount}
                </p>
              </div>
              <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700" data-id="element-2298">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1" data-id="element-2299">
                  Empleados Registrados
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-2300">
                  {employeeCount}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="p-6 border-t border-gray-200 dark:border-gray-800 sticky bottom-0 bg-white dark:bg-gray-900 rounded-b-2xl" data-id="element-2301">
          <Button onClick={onClose} fullWidth data-id="element-2302">
            Cerrar
          </Button>
        </div>
      </div>
    </div>;
}